import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CardPreview } from "./Customizer";
import { Sparkles, ArrowRight, Loader2, Heart, Flag, X, Check } from "lucide-react";

export default function Recipient() {
  const { token } = useParams();
  const [greeting, setGreeting] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reported, setReported] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [sentCount, setSentCount] = useState(null);

  useEffect(() => {
    api.get(`/greetings/${token}`)
      .then(({ data }) => setGreeting(data))
      .catch(() => setNotFound(true));
  }, [token]);

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setSentCount(data.sent ?? data.greetings ?? 0)).catch(() => {});
  }, []);

  const submitReport = async () => {
    setReporting(true);
    try {
      await api.post(`/greetings/${token}/report`, { reason });
      setReported(true);
    } catch (e) { /* ignore */ } finally {
      setReporting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center px-5">
        <div className="nb-card p-10 text-center max-w-md">
          <h1 className="text-2xl font-display font-bold">Greeting not found</h1>
          <p className="text-[#4A4A4A] mt-2">This link may have expired.</p>
          <Link to="/" className="nb-btn bg-[#FFB5A7] px-6 py-3 mt-5 inline-flex">Go to SendOccasions</Link>
        </div>
      </div>
    );
  }

  if (!greeting) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen">
      <header className="border-b-2 border-[#0A0A0A] bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-xl">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#FFB5A7] border-2 border-[#0A0A0A]"><Sparkles className="w-5 h-5" /></span>
            SendOccasions
          </Link>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-5 py-12 text-center">
        <div className="inline-flex items-center gap-2 nb-card px-4 py-1.5 bg-[#F9F871] mb-6 animate-bounce-soft">
          <Heart className="w-4 h-4" /> <span className="text-xs font-bold uppercase tracking-widest">You've got a greeting!</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold mb-6" data-testid="recipient-heading">
          {greeting.sender_name} sent you something special
        </h1>

        <div className="animate-pop-in">
          <CardPreview
            template={{ image_url: greeting.template_image, title: greeting.template_title, category: greeting.category }}
            message={greeting.message} recipient={greeting.recipient_name}
            sender={greeting.sender_name} photoUrl={greeting.photo_url} bg={greeting.bg_color}
            gradient={greeting.template_gradient}
          />
        </div>

        <div className="nb-card nb-shadow-lg p-7 mt-10 bg-[#FFB5A7]">
          <h2 className="text-2xl font-display font-bold">Want to send one too?</h2>
          <p className="text-[#0A0A0A]/80 mt-2">Create your own free greeting in seconds — add your photo and message.</p>
          {sentCount !== null && (
            <div data-testid="recipient-sent-counter" className="inline-flex items-center gap-2 nb-card bg-white px-4 py-2 mt-5 font-bold">
              <Sparkles className="w-4 h-4 text-[#FF6A88]" />
              <span className="tabular-nums">{sentCount.toLocaleString()}</span>
              <span className="font-semibold text-[#0A0A0A]/80">greetings sent &amp; counting</span>
            </div>
          )}
          <div>
            <Link to="/signup" data-testid="recipient-cta" className="nb-btn bg-white px-7 py-3.5 mt-5 inline-flex text-lg">
              Create Your Own Greeting <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>

        <button onClick={() => setReportOpen(true)} data-testid="report-greeting-btn"
          className="inline-flex items-center gap-1.5 text-sm text-[#4A4A4A] font-semibold mt-6 hover:text-[#0A0A0A] transition-colors">
          <Flag className="w-4 h-4" /> Report this greeting
        </button>
      </div>

      {reportOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm px-4" data-testid="report-modal" onClick={() => setReportOpen(false)}>
          <div className="nb-card nb-shadow-lg w-full max-w-md p-6 animate-pop-in" onClick={(e) => e.stopPropagation()}>
            {reported ? (
              <div className="text-center">
                <div className="w-14 h-14 grid place-items-center rounded-xl bg-[#A8E6CF] border-2 border-[#0A0A0A] mx-auto"><Check className="w-7 h-7" /></div>
                <h3 className="text-xl font-display font-bold mt-4">Thanks for reporting</h3>
                <p className="text-[#4A4A4A] mt-2">Our team will review this greeting. We take abuse seriously.</p>
                <button onClick={() => setReportOpen(false)} data-testid="report-close" className="nb-btn bg-[#FFB5A7] px-6 py-3 mt-5">Close</button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-display font-bold flex items-center gap-2"><Flag className="w-5 h-5" /> Report greeting</h3>
                  <button onClick={() => setReportOpen(false)} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-[#0A0A0A] bg-white"><X className="w-4 h-4" /></button>
                </div>
                <p className="text-sm text-[#4A4A4A]">Tell us what's wrong with this greeting (abusive, harassing, inappropriate, etc.).</p>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} data-testid="report-reason"
                  placeholder="Reason for reporting…"
                  className="w-full nb-border rounded-xl px-4 py-3 bg-[#FDFBF7] mt-3 resize-none focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" />
                <button onClick={submitReport} disabled={reporting} data-testid="report-submit" className="nb-btn bg-[#FFB5A7] w-full py-3 mt-4">
                  {reporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Flag className="w-5 h-5" />} Submit report
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
